(function (root, factory) {
  'use strict';
  const api = factory(root);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root && root.document) api.mount();
})(typeof window !== 'undefined' ? window : null, function (root) {
  'use strict';

  const cartAddUrl = (rootUrl) => {
    const routeRoot = rootUrl || '/';
    return `${routeRoot.endsWith('/') ? routeRoot : `${routeRoot}/`}cart/add.js`;
  };
  const cartChangeUrl = (rootUrl) => cartAddUrl(rootUrl).replace('cart/add.js', 'cart/change.js');

  const addErrorMessage = (payload, fallback) => {
    const message = payload && (payload.description || payload.message);
    return typeof message === 'string' && message.trim() ? message : fallback;
  };

  async function submitCartAdd({ fetcher, rootUrl, formData, sectionsUrl, fallbackError }) {
    formData.set('sections', 'cart-drawer,cart-icon-bubble');
    formData.set('sections_url', sectionsUrl);

    let response;
    let payload;
    try {
      response = await fetcher(cartAddUrl(rootUrl), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      payload = await response.json();
    } catch (_error) {
      return { status: 'uncertain', message: fallbackError };
    }

    if (!response.ok || Number(payload && payload.status) >= 400) {
      return { status: 'rejected', message: addErrorMessage(payload, fallbackError) };
    }

    const item = payload && (Array.isArray(payload.items) ? payload.items[0] : payload);
    if (!item || !item.id) {
      return { status: 'uncertain', message: fallbackError };
    }

    const sections = payload.sections;
    if (!sections
      || typeof sections['cart-drawer'] !== 'string'
      || typeof sections['cart-icon-bubble'] !== 'string') {
      return { status: 'added-unrendered' };
    }

    return { status: 'added', sections, itemKey: item.key || '' };
  }

  async function submitCartChange({ fetcher, rootUrl, itemKey, quantity, sectionsUrl, fallbackError }) {
    let response;
    let payload;
    try {
      response = await fetcher(cartChangeUrl(rootUrl), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: itemKey,
          quantity,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: sectionsUrl,
        }),
      });
      payload = await response.json();
    } catch (_error) {
      return { status: 'uncertain', message: fallbackError };
    }

    if (!response.ok || Number(payload && payload.status) >= 400) {
      return { status: 'rejected', message: addErrorMessage(payload, fallbackError) };
    }
    if (!payload || !Number.isInteger(payload.item_count) || !Array.isArray(payload.items)) {
      return { status: 'uncertain', message: fallbackError };
    }
    const sections = payload.sections;
    if (!sections || typeof sections['cart-drawer'] !== 'string'
      || typeof sections['cart-icon-bubble'] !== 'string') {
      return { status: 'changed-unrendered' };
    }

    const changedLine = payload.items.find((item) => item.key === itemKey);
    let status = 'changed';
    if (quantity > 0 && payload.item_count === 0) status = 'unexpectedly-empty';
    else if (changedLine && changedLine.quantity !== quantity) status = 'changed-mismatch';
    else if (quantity === 0 && changedLine) status = 'changed-mismatch';
    return { status, sections, itemCount: payload.item_count };
  }

  function setLineFeedback(line, message, state) {
    if (!line) return false;
    const feedback = line.querySelector('[data-yl-cart-drawer-line-feedback]');
    if (!feedback) return false;
    line.toggleAttribute('data-yl-cart-drawer-pending', state === 'pending');
    // 读代码推断（2026-09-24）：setDrawerBusy 用全局 live region 播报 pending；
    // 行内只显示固定尺寸 spinner，避免新增文字行推动手机抽屉。
    feedback.textContent = state === 'pending' ? '' : (message || '');
    feedback.hidden = state === 'pending' || !message;
    feedback.setAttribute('role', state === 'error' ? 'alert' : 'status');
    return true;
  }

  async function fetchCartSections({ fetcher, cartUrl, diagnose }) {
    const separator = cartUrl.includes('?') ? '&' : '?';
    // 实测（2026-09-24）：Shopify 在此 GET 带 Accept: application/json 时返回 cart JSON，
    // 而不是 sections 对象；让浏览器使用默认 Accept 才会返回渲染后的抽屉和角标。
    const response = await fetcher(`${cartUrl}${separator}sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) {
      diagnose?.('section-http-error');
      return null;
    }
    const sections = await response.json();
    if (!sections || typeof sections['cart-drawer'] !== 'string'
      || typeof sections['cart-icon-bubble'] !== 'string') {
      diagnose?.('section-response-shape');
      return null;
    }
    return sections;
  }

  function mount() {
    const document = root.document;
    const dialog = document.querySelector('[data-yl-cart-drawer]');
    if (!dialog) return;

    let lastTrigger = null;
    let busy = false;
    let needsReview = false;
    const cartUrl = dialog.dataset.cartUrl;
    const debugCart = new URLSearchParams(root.location.search).get('codex_debug') === 'cart';
    const diagnose = (code) => {
      if (debugCart) root.console.warn('[YoloLife cart drawer]', code);
    };
    const canEnhance = typeof root.fetch === 'function'
      && typeof root.FormData === 'function'
      && typeof root.DOMParser === 'function'
      && typeof dialog.showModal === 'function';

    const showError = (form, message, uncertain) => {
      let error = form.querySelector('[data-yl-add-error]');
      if (!error) {
        error = document.createElement('p');
        error.className = 'yl-cart-drawer__add-error';
        error.dataset.ylAddError = '';
        error.setAttribute('role', 'alert');
        form.append(error);
      }
      error.textContent = message;
      if (uncertain) {
        const link = document.createElement('a');
        link.href = cartUrl;
        link.textContent = dialog.dataset.viewCartLabel;
        error.append(' ', link);
      }
      error.hidden = false;
    };

    const setStatus = (message) => {
      const status = dialog.querySelector('[data-yl-cart-drawer-status]');
      if (status) status.textContent = message;
    };

    const showDrawerError = (message, review) => {
      const error = dialog.querySelector('[data-yl-cart-drawer-error]');
      if (!error) return;
      error.textContent = message;
      if (review) {
        const link = document.createElement('a');
        link.href = cartUrl;
        link.textContent = dialog.dataset.viewCartLabel;
        error.append(' ', link);
      }
      error.hidden = false;
      setStatus('');
    };

    const setDrawerBusy = (isBusy) => {
      dialog.toggleAttribute('aria-busy', isBusy);
      const isLocked = isBusy || needsReview;
      dialog.querySelectorAll('[data-yl-cart-drawer-action]').forEach((button) => {
        const line = button.closest('[data-yl-cart-drawer-line]');
        const quantity = Number(line && line.dataset.ylCartDrawerQuantity);
        const minimum = Number(line && line.dataset.ylCartDrawerMin) || 1;
        const maximum = line && line.dataset.ylCartDrawerMax
          ? Number(line.dataset.ylCartDrawerMax) : Number.POSITIVE_INFINITY;
        button.disabled = isLocked || (button.dataset.ylCartDrawerAction === 'decrease'
          ? quantity <= minimum : quantity >= maximum);
      });
      dialog.querySelectorAll('[data-yl-cart-drawer-remove]').forEach((link) => {
        if (isLocked) link.setAttribute('aria-disabled', 'true');
        else link.removeAttribute('aria-disabled');
      });
      const checkout = dialog.querySelector('.yl-cart-drawer__checkout');
      if (checkout) checkout.disabled = isLocked;
      if (isBusy) setStatus(dialog.dataset.updatingLabel);
      if (!isBusy && needsReview) {
        dialog.querySelector('[data-yl-cart-drawer-error] a')?.focus({ preventScroll: true });
      }
    };

    const replaceCart = (sections) => {
      const parser = new root.DOMParser();
      const returnedDrawer = parser.parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('[data-yl-cart-drawer]');
      const returnedLink = parser.parseFromString(sections['cart-icon-bubble'], 'text/html')
        .querySelector('[data-yl-cart-link]');
      const currentLink = document.querySelector('[data-yl-cart-link]');
      if (!returnedDrawer || !Number.isInteger(Number(returnedDrawer.dataset.itemCount))
        || Number(returnedDrawer.dataset.itemCount) < 0 || !returnedLink || !currentLink) {
        diagnose('rendered-section-unusable');
        return false;
      }
      dialog.innerHTML = returnedDrawer.innerHTML;
      dialog.dataset.itemCount = returnedDrawer.dataset.itemCount;
      currentLink.replaceWith(returnedLink.cloneNode(true));
      return true;
    };

    const reconcileCart = async () => {
      const sections = await fetchCartSections({
        fetcher: root.fetch.bind(root),
        cartUrl,
        diagnose,
      });
      return Boolean(sections && replaceCart(sections));
    };

    const restoreLineFocus = (key, index, action) => {
      const lines = Array.from(dialog.querySelectorAll('[data-yl-cart-drawer-line]'));
      const line = lines.find((candidate) => candidate.dataset.ylCartDrawerLine === key)
        || lines[Math.min(index - 1, lines.length - 1)];
      const target = line && (line.querySelector(`[data-yl-cart-drawer-action="${action}"]`)
        || line.querySelector('[data-yl-cart-drawer-action], [data-yl-cart-drawer-remove]'));
      (target || dialog.querySelector('[data-yl-cart-drawer-title]'))?.focus({ preventScroll: true });
    };

    const findLine = (key, index) => {
      const lines = Array.from(dialog.querySelectorAll('[data-yl-cart-drawer-line]'));
      return lines.find((candidate) => candidate.dataset.ylCartDrawerLine === key)
        || lines[Math.min(index - 1, lines.length - 1)];
    };

    const changeLine = async (line, quantity, action) => {
      if (busy || needsReview) return;
      busy = true;
      const key = line.dataset.ylCartDrawerLine;
      const index = Number(line.dataset.ylCartDrawerIndex) || 1;
      setLineFeedback(line, dialog.dataset.updatingLabel, 'pending');
      setDrawerBusy(true);
      try {
        const result = await submitCartChange({
          fetcher: root.fetch.bind(root),
          rootUrl: dialog.dataset.rootUrl,
          itemKey: key,
          quantity,
          sectionsUrl: `${root.location.pathname}${root.location.search}`,
          fallbackError: dialog.dataset.updateError,
        });
        if (result.status === 'changed-unrendered') {
          root.location.assign(cartUrl);
          return;
        }
        if (result.sections) {
          if (!replaceCart(result.sections)) {
            root.location.assign(cartUrl);
            return;
          }
          const heading = dialog.querySelector('[data-yl-cart-drawer-title]');
          if (heading) heading.textContent = dialog.dataset.cartLabel;
          if (result.status === 'changed') setStatus(dialog.dataset.updatedLabel);
          else if (result.status === 'changed-mismatch'
            && setLineFeedback(findLine(key, index), dialog.dataset.updateError, 'error')) {
            setStatus('');
          } else showDrawerError(result.status === 'unexpectedly-empty'
            ? dialog.dataset.removedOnUpdate : dialog.dataset.updateError, true);
          restoreLineFocus(key, index, action);
          return;
        }

        let reconciled = false;
        // 读代码推断（2026-09-23）：GET 也失败时金额不可确认，保留原展示并要求去完整购物车复核。
        try { reconciled = await reconcileCart(); } catch (_error) { /* review path below */ }
        needsReview = !reconciled;
        const scopedFeedback = setLineFeedback(findLine(key, index),
          result.message || dialog.dataset.updateError, 'error');
        if (!reconciled) showDrawerError(dialog.dataset.updateError, true);
        else if (!scopedFeedback) showDrawerError(result.message || dialog.dataset.updateError, false);
        restoreLineFocus(key, index, action);
      } catch (_error) {
        needsReview = true;
        showDrawerError(dialog.dataset.updateError, true);
      } finally {
        busy = false;
        const currentLine = findLine(key, index);
        if (currentLine && currentLine.hasAttribute('data-yl-cart-drawer-pending')) {
          setLineFeedback(currentLine, '', 'idle');
        }
        setDrawerBusy(false);
      }
    };

    const open = (trigger, addedItemKey) => {
      lastTrigger = trigger;
      const heading = dialog.querySelector('[data-yl-cart-drawer-title]');
      if (heading) {
        heading.textContent = addedItemKey === null ? dialog.dataset.cartLabel : dialog.dataset.addedLabel;
      }
      if (!dialog.open) dialog.showModal();
      if (heading) heading.focus({ preventScroll: true });
      if (addedItemKey) {
        const line = Array.from(dialog.querySelectorAll('[data-yl-cart-drawer-line]'))
          .find((candidate) => candidate.dataset.ylCartDrawerLine === addedItemKey);
        if (line) line.scrollIntoView({ block: 'nearest' });
      }
    };

    document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches('.yl-species-card__form, .yl-product-cta__form, .bundle-buy-form') || !canEnhance) return;
      event.preventDefault();
      const trigger = event.submitter || form.querySelector('[type="submit"]');
      const bundleSection = form.matches('.bundle-buy-form') ? form.closest('[data-bundle-section]') : null;
      if (bundleSection && Date.now() >= Date.parse(bundleSection.dataset.end)) {
        if (trigger) { trigger.disabled = true; trigger.textContent = bundleSection.dataset.ended; }
        return;
      }
      if (busy || trigger?.disabled) return;
      busy = true;
      const buttons = Array.from(document.querySelectorAll(
        '.yl-species-card__form button[type="submit"], .yl-product-cta__form button[type="submit"], .bundle-buy-form button[type="submit"]'
      )).filter((button) => !button.disabled);
      buttons.forEach((button) => { button.disabled = true; });
      form.setAttribute('aria-busy', 'true');
      const oldError = form.querySelector('[data-yl-add-error]');
      if (oldError) oldError.hidden = true;

      try {
        const result = await submitCartAdd({
          fetcher: root.fetch.bind(root),
          rootUrl: dialog.dataset.rootUrl,
          formData: new root.FormData(form),
          sectionsUrl: `${root.location.pathname}${root.location.search}`,
          fallbackError: dialog.dataset.addError,
        });

        if (result.status === 'rejected' || result.status === 'uncertain') {
          showError(form, result.message, result.status === 'uncertain');
          return;
        }
        if (result.status === 'added-unrendered' || !replaceCart(result.sections)) {
          root.location.assign(cartUrl);
          return;
        }
        open(trigger, result.itemKey);
      } catch (_error) {
        showError(form, dialog.dataset.addError, true);
      } finally {
        form.removeAttribute('aria-busy');
        buttons.forEach((button) => { button.disabled = false; });
        busy = false;
      }
    }, true);

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('[data-yl-cart-link]');
      if (cartLink && canEnhance && !document.querySelector('yololife-cart')) {
        event.preventDefault();
        open(cartLink, null);
        return;
      }
      if (event.target.closest('[data-yl-cart-drawer-close], [data-yl-cart-drawer-continue]')) {
        dialog.close();
      }
    });

    dialog.addEventListener('click', (event) => {
      const actionButton = event.target.closest('[data-yl-cart-drawer-action]');
      const removeLink = event.target.closest('[data-yl-cart-drawer-remove]');
      if ((!actionButton && !removeLink) || !canEnhance) return;
      event.preventDefault();
      if (busy || needsReview) return;
      const line = (actionButton || removeLink).closest('[data-yl-cart-drawer-line]');
      if (!line) return;
      const current = Number(line.dataset.ylCartDrawerQuantity);
      const minimum = Number(line.dataset.ylCartDrawerMin) || 1;
      const increment = Number(line.dataset.ylCartDrawerIncrement) || 1;
      const maximum = line.dataset.ylCartDrawerMax
        ? Number(line.dataset.ylCartDrawerMax) : Number.POSITIVE_INFINITY;
      const action = removeLink ? 'remove' : actionButton.dataset.ylCartDrawerAction;
      const quantity = removeLink ? 0 : Math.max(minimum, Math.min(maximum,
        current + (action === 'increase' ? increment : -increment)));
      if (quantity === current) return;
      changeLine(line, quantity, action);
    });

    dialog.addEventListener('submit', (event) => {
      if (!event.target.matches('.yl-cart-drawer__checkout-form')) return;
      if (!busy && !needsReview) return;
      event.preventDefault();
      if (needsReview) root.location.assign(cartUrl);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener('close', () => {
      const trigger = lastTrigger && lastTrigger.isConnected
        ? lastTrigger : document.querySelector('[data-yl-cart-link]');
      if (trigger) trigger.focus({ preventScroll: true });
      lastTrigger = null;
    });
  }

  return { cartAddUrl, cartChangeUrl, submitCartAdd, submitCartChange, setLineFeedback, fetchCartSections, mount };
});
